import { Box, Stack, Typography } from '@mui/material';
import { useDisplayTime } from 'src/utils/hooks/use-display-time';
import {
  closureNoteRangeSx,
  closureNoteReopensSx,
  closureNoteSx,
  hoursCardCaptionSx,
  hoursCardFooterSx,
  hoursCardHeadlineSx,
  hoursCardSx,
  hoursRowSx,
} from './partner-business-status.styles';
import { PartnerBusinessStatus } from './partner-business-status.types';
import {
  buildBoundaryLine,
  buildClosureNote,
  buildPartnerTimeFooter,
  buildWeeklyHoursRows,
} from './partner-business-status.utils';

interface PartnerBusinessHoursCardProps {
  status: PartnerBusinessStatus;
}

export const PartnerBusinessHoursCard = ({ status }: PartnerBusinessHoursCardProps) => {
  const { displayTime, timeFormat } = useDisplayTime({
    tz: status.timeZone,
    padHours: true,
  });

  const boundaryLine = buildBoundaryLine(status, displayTime);
  const closure = buildClosureNote(status, displayTime);

  return (
    <Box sx={hoursCardSx} data-testid="partner-business-hours-card">
      {boundaryLine ? (
        <Typography
          component="div"
          sx={hoursCardHeadlineSx(status.state)}
          data-testid="partner-business-hours-headline"
        >
          {boundaryLine}
        </Typography>
      ) : null}

      {closure ? (
        <Box sx={closureNoteSx} data-testid="partner-business-closure-note">
          <Box component="span" data-testid="partner-business-closure-name">
            {closure.name}
          </Box>
          <Box
            component="span"
            sx={closureNoteRangeSx}
            data-testid="partner-business-closure-range"
          >
            {closure.closedRange}
          </Box>
          {closure.reopens ? (
            <Box
              component="span"
              sx={closureNoteReopensSx}
              data-testid="partner-business-closure-reopens"
            >
              {closure.reopens}
            </Box>
          ) : null}
        </Box>
      ) : null}

      <Typography component="span" sx={hoursCardCaptionSx}>
        Business hours
      </Typography>

      {buildWeeklyHoursRows(status, timeFormat).map((row) => (
        <Box
          key={row.weekDay}
          sx={hoursRowSx(row.isToday)}
          data-testid={`partner-business-hours-row-${row.weekDay}`}
          data-today={row.isToday || undefined}
        >
          <Box component="span">{row.dayLabel}</Box>
          <Stack
            component="span"
            alignItems="flex-end"
            data-testid={`partner-business-hours-windows-${row.weekDay}`}
          >
            {row.windows.map((window) => (
              <Box component="span" key={window} data-testid="partner-business-hours-window">
                {window}
              </Box>
            ))}
          </Stack>
        </Box>
      ))}

      <Typography
        component="span"
        sx={hoursCardFooterSx}
        data-testid="partner-business-hours-footer"
      >
        {buildPartnerTimeFooter(status)}
      </Typography>
    </Box>
  );
};
